// THE CURSOR-PAGE ACCUMULATOR — one fold rule for every "Load more" list.
//
// A screen that walks cursor pages keeps the rows it has already seen and adds
// the next page to them. There are exactly two ways to write that fold and they
// behave DIFFERENTLY on a refetch:
//
//   append-if-unseen   `[...prior, ...next.filter(r => !seen.has(r.id))]`
//   fold-by-id         `map.set(r.id, r)` over the prior rows
//
// They agree while nothing changes and disagree the moment a row is UPDATED —
// which is exactly what a mutation on this screen does. Every waste/production
// mutation invalidates its list, react-query refetches page 1, and the fresh
// page carries the SAME ids with NEW values. `append-if-unseen` sees a known id
// and throws the update on the floor: the write reached the server, the row on
// screen never moved. The user's natural response is to press the control
// again, which the server refuses as already-decided — so a write that WORKED
// is reported to them as a write that FAILED.
//
// Measured (surface review, 2026-08-13, waste ledger `WE-2026-000062`): server
// `pending → reuse`, screen still "Not decided yet", drawer still offering the
// form, second attempt `409 DISPOSITION_FINAL` → "Could not save the decision".
// The remnant registry — the same area, the same page-walk shape, written by
// the same slice — had the fold-by-id form and behaved correctly. Two rules for
// one job in one area is the defect; this module is the one rule.
//
// ORDER IS THE SERVER'S. `Map.set` on an EXISTING key updates in place and
// keeps the key's original position, so an updated row never jumps to the
// bottom of a list the server ordered (the ledger is `occurred_at DESC`, the
// registry `length_m DESC`). New ids append in page order, which is the order
// the cursor walk handed them over.

/** A row a paged list can fold: anything with a stable server id. */
export interface Identified {
  readonly id: string;
}

/**
 * Fold one freshly-fetched page over the rows already accumulated.
 *
 * Pure, order-preserving, and total: `prior` may be empty (first page) and
 * `incoming` may re-state rows already held (a refetch of page 1) or introduce
 * new ones (the next cursor page).
 */
export function foldPagedRows<T extends Identified>(
  prior: readonly T[],
  incoming: readonly T[],
): T[] {
  const byId = new Map<string, T>();
  for (const row of prior) byId.set(row.id, row);
  for (const row of incoming) byId.set(row.id, row);
  return [...byId.values()];
}
